import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, BriefcaseBusiness, Building2, ChevronRight, Globe2, Hash, Lock, MapPin, MoreHorizontal, Pencil, Plus, Upload } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import ProjectActionDialog from "../components/ProjectActionDialog";
import apiClient, { getFriendlyErrorMessage } from "../../../app/apiClient";
import { normalizeProject } from "./projectDirectory";
import ProjectUpdates from "../components/ProjectUpdates";
import ProjectImages from "../components/ProjectImages";
import ProjectPublishDialog from "../components/ProjectPublishDialog";
import { projectPublishApi } from "../projectPublishApi";
import { getCoverImageId, normalizeMedia, projectMediaApi } from "../projectMediaApi";
import { projectUpdatesApi } from "../projectUpdatesApi";
import "./ProjectDetails.css";
import Documents from "../../documents/Documents";
import { formatRupees } from "../../../utils/formatRupees";

const tabs = ["Overview", "Images", "Updates", "Documents"];
const missing = (value) => value === null || value === undefined || value === "" ? "Not set" : value;
function date(value) {
  const parsed = new Date(value);
  return value && !Number.isNaN(parsed.getTime()) ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "long", year: "numeric" }).format(parsed) : "Not set";
}

function normalizeDetails(raw) {
  const project = normalizeProject(raw);
  const client = raw.client || {};
  return {
    ...project,
    clientId: raw.clientId ?? client.id ?? client.userId,
    clientCode: raw.clientCode || client.clientCode,
    clientEmail: raw.clientEmail || client.email,
    clientPhone: raw.clientPhone || client.phoneNumber || client.phone,
    expectedEndDate: raw.expectedEndDate || raw.expected_end_date,
    actualEndDate: raw.actualEndDate || raw.actual_end_date,
    updatedAt: raw.updatedAt || raw.updated_at,
    createdByName: raw.createdByName || raw.createdBy?.userName || raw.createdBy?.fullName || raw.createdBy?.name,
    coverImageId: getCoverImageId(raw),
    images: normalizeMedia(raw),
    updates: Array.isArray(raw.updates) ? raw.updates.filter((item) => item && typeof item === "object").slice().sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0)) : [],
  };
}
function Badge({ project, visibility = false }) {
  const label = visibility ? (project.isPublic ? "Public" : "Private") : project.status;
  return <span className={"detail-badge detail-badge-" + label.toLowerCase().replace(/ /g, "-")}>
    {visibility ? (project.isPublic ? <Globe2 size={12} /> : <Lock size={12} />) : <i />}{label}
  </span>;
}
function Field({ label, children }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>;
}

export default function ProjectDetails() {
  const { id } = useParams();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatesError, setUpdatesError] = useState("");
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [updatesAttempt, setUpdatesAttempt] = useState(0);
  const [imagesError, setImagesError] = useState("");
  const [imagesLoading, setImagesLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [tab, setTab] = useState("Overview");
  const [action, setAction] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const moreButton = useRef(null);
  const moreMenu = useRef(null);
  useEffect(() => {
    if (!moreOpen) return;
    moreMenu.current?.querySelector("[role=menuitem]")?.focus();
    const outside = (event) => { if (!moreMenu.current?.contains(event.target)) setMoreOpen(false); };
    const escape = (event) => { if (event.key === "Escape") { setMoreOpen(false); moreButton.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [moreOpen]);
  const [success, setSuccess] = useState("");
  useEffect(() => { setTab(location.state?.tab === "Updates" ? "Updates" : "Overview"); setAction(location.state?.editProject ? "edit" : null); setSuccess(location.state?.success || ""); }, [id, location.state?.editProject, location.state?.tab, location.state?.success]);
  const returnState = { activeItem: "Projects", scrollY: location.state?.scrollY || 0 };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setImagesError("");
    setImagesLoading(true);
    setProject(null);
    apiClient.get("/admin/project/" + id).then(({ data }) => {
      const raw = data?.project || data?.data || data;
      if (!raw || typeof raw !== "object" || Array.isArray(raw) || Object.keys(raw).length === 0) throw new Error("Project not found.");
      if (!active) return;
      setProject(normalizeDetails({ ...raw, id: raw.id ?? id, images: [] }));
      // Render the project immediately; the separate media request must not block it.
      projectMediaApi.list(id).then((images) => {
        if (active) setProject((current) => ({ ...current, images: normalizeMedia({ ...current, images }) }));
      }).catch((requestError) => {
        if (active) setImagesError(getFriendlyErrorMessage(requestError, "Unable to load project images."));
      }).finally(() => { if (active) setImagesLoading(false); });
    }).catch((requestError) => {
      if (active) setError(getFriendlyErrorMessage(requestError, "Unable to load project details."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, attempt]);

  useEffect(() => {
    if (!project?.id) return;
    let active = true;
    setUpdatesLoading(true); setUpdatesError("");
    projectUpdatesApi.list(id).then((updates) => {
      if (active) setProject((current) => ({ ...current, updates }));
    }).catch((failure) => { if (active) setUpdatesError(getFriendlyErrorMessage(failure, "Unable to load project updates.")); })
      .finally(() => { if (active) setUpdatesLoading(false); });
    return () => { active = false; };
  }, [id, project?.id, attempt, updatesAttempt]);

  const breadcrumb = <nav className="detail-breadcrumb" aria-label="Breadcrumb">
    <Link to="/admin/dashboard">Admin</Link><ChevronRight size={13} /><Link to="/admin/projects" state={returnState}>Projects</Link><ChevronRight size={13} /><strong aria-current="page">{project?.projectCode || id}</strong>
  </nav>;

  const selectTabWithKey = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = tabs.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setTab(tabs[next]);
    document.getElementById("detail-tab-" + tabs[next])?.focus();
  };

  return <AdminLayout activeItem="Projects" breadcrumb={breadcrumb}>
    <div className="project-detail-page">
      {success && <p className="detail-success" role="status">{success}</p>}
      {loading ? <div className="detail-state">Loading project details...</div> : error || !project ? <div className="detail-state" role="alert"><h1>Unable to load project</h1><p>{error || "Project not found."}</p><button className="detail-button" onClick={() => setAttempt((value) => value + 1)}>Try again</button><Link to="/admin/projects" state={returnState}>Back to projects</Link></div> : <>
        <section className="detail-hero">
          <div className="detail-hero-content">
            <div className="detail-identity">
              <p className="detail-updated"><strong>PROJECT</strong><span>•</span>{project.updatedAt ? "Last updated " + date(project.updatedAt) : "Not updated yet"}</p>
              <h1>{project.projectName}</h1>
              <div className="detail-meta"><span><Hash size={14} />{project.projectCode}</span><span><Building2 size={14} />{project.clientName}</span><span><MapPin size={14} />{missing(project.location)}</span></div>
              <div className="detail-badges"><Badge project={project} /><Badge project={project} visibility /></div>
            </div>
            <div className="detail-actions">
              <Link className="detail-button" to={"/admin/projects/" + id + "/edit"}><Pencil size={14} />Edit Project</Link>
              <button className="detail-button" onClick={() => setAction("photos")}><Upload size={14} />Upload Photos</button>
              <Link className="detail-button detail-primary" to={"/admin/projects/" + id + "/updates/new"}><Plus size={14} />Add Update</Link>
              <div className="detail-actions-menu" ref={moreMenu}><button ref={moreButton} className="detail-button detail-more" aria-label="More project actions" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)}><MoreHorizontal size={16} /></button>{moreOpen && <div className="detail-actions-popover" role="menu"><button role="menuitem" onClick={() => { setMoreOpen(false); setPublishOpen(true); }}>{project.isPublic ? <Lock size={15} /> : <Globe2 size={15} />}{project.isPublic ? "Unpublish Project" : "Publish Project"}</button></div>}</div>
            </div>
          </div>
          <div className="detail-tabs" role="tablist" aria-label="Project sections">
            {tabs.map((name, index) => <button key={name} id={"detail-tab-" + name} role="tab" aria-selected={tab === name} aria-controls={"detail-panel-" + name} tabIndex={tab === name ? 0 : -1} onClick={() => setTab(name)} onKeyDown={(event) => selectTabWithKey(event, index)}>
              {name}{["Images", "Updates"].includes(name) && <span>{name === "Images" ? project.images.length : project.updates.length}</span>}
            </button>)}
          </div>
        </section>

        <div id="detail-panel-Overview" role="tabpanel" aria-labelledby="detail-tab-Overview" hidden={tab !== "Overview"} tabIndex={0}>
          <div className="detail-overview">
            <article className="detail-card">
              <header className="detail-card-heading"><div><h2>Project Information</h2><p>Core details and administrative configuration</p></div><Link className="detail-edit" to={"/admin/projects/" + id + "/edit"}><Pencil size={13} />Edit</Link></header>
              <div className="detail-card-body">
                <dl className="detail-fields">
                  <Field label="Project ID">{project.projectCode}</Field><Field label="Project Name">{project.projectName}</Field><Field label="Category"><span className="detail-category">{project.category.toLowerCase() || "Not set"}</span></Field>
                  <Field label="Location">{missing(project.location)}</Field><Field label="Start Date">{date(project.startDate)}</Field><Field label="Expected Completion">{date(project.expectedEndDate)}</Field>
                  <Field label="Project Cost">{formatRupees(project.cost)}</Field><Field label="Status"><Badge project={project} /></Field><Field label="Visibility"><Badge project={project} visibility /></Field>
                </dl>
                <dl className="detail-description"><Field label="Description">{project.description || "No description has been added for this project yet."}</Field></dl>
                <details className="detail-additional"><summary>Additional project details</summary><dl className="detail-fields"><Field label="Address">{missing(project.address)}</Field><Field label="Built-up area">{missing(project.area)}</Field><Field label="Foundation">{missing(project.foundation)}</Field><Field label="Created">{date(project.createdAt)}</Field><Field label="Created by">{missing(project.createdByName)}</Field><Field label="Actual completion">{date(project.actualEndDate)}</Field></dl></details>
              </div>
            </article>
            <aside className="detail-card detail-client">
              <header className="detail-card-heading"><div><h2>Client Information</h2><p>Assigned project client</p></div><span className="detail-client-icon"><BriefcaseBusiness size={17} /></span></header>
              <div className="detail-card-body">
                {project.clientId !== null && project.clientId !== undefined ? <><dl>
                  <Field label="Client ID"><span className="detail-client-code">{project.clientCode || project.clientId}</span></Field><Field label="Client Name">{project.clientName}</Field>
                  <Field label="Email">{project.clientEmail ? <a href={"mailto:" + project.clientEmail}>{project.clientEmail}</a> : "Not set"}</Field>
                  <Field label="Phone">{project.clientPhone ? <a href={"tel:" + project.clientPhone}>{project.clientPhone}</a> : "Not set"}</Field>
                </dl><Link className="detail-button detail-client-link" to="/admin/clients" state={{ clientSearch: project.clientEmail || project.clientName }}>View Client<ArrowUpRight size={14} /></Link></> : <p className="detail-empty">No client is assigned yet.</p>}
              </div>
            </aside>
          </div>
        </div>

        <section id="detail-panel-Images" role="tabpanel" aria-labelledby="detail-tab-Images" hidden={tab !== "Images"} tabIndex={0} className="detail-card detail-tab-content">
          {imagesLoading ? <p className="detail-empty">Fetching project images...</p> : imagesError ? <div role="alert"><p>{imagesError}</p><button className="detail-button" onClick={() => setAttempt((value) => value + 1)}>Retry images</button></div> : <ProjectImages key={project.id} project={project} onImagesChange={(images, changes = {}) => setProject((current) => ({ ...current, ...changes, images }))} onUpload={() => setAction("photos")} />}
        </section>
        <section id="detail-panel-Documents" role="tabpanel" aria-labelledby="detail-tab-Documents" hidden={tab !== "Documents"} tabIndex={0}>
          {tab === "Documents" && <Documents key={id} projectId={id} admin />}
        </section>
        <section id="detail-panel-Updates" role="tabpanel" aria-labelledby="detail-tab-Updates" hidden={tab !== "Updates"} tabIndex={0} className="detail-card project-updates-panel">
          <ProjectUpdates key={project.id} projectId={id} updates={project.updates} loading={updatesLoading} error={updatesError} onRetry={() => setUpdatesAttempt((value) => value + 1)} />
        </section>
      </>}
      {publishOpen && project && <ProjectPublishDialog project={project} imagesLoading={imagesLoading} imagesError={imagesError} onClose={() => { setPublishOpen(false); moreButton.current?.focus(); }} onCoverChange={(coverImageId) => setProject((current) => ({ ...current, coverImageId, images: normalizeMedia({ ...current, coverImageId, images: current.images }) }))} onSubmit={async (payload) => {
        const publishing = !project.isPublic;
        if (publishing) await projectPublishApi.publish(payload);
        else await projectPublishApi.unpublish(project.id);
        setProject((current) => ({ ...current, isPublic: publishing, images: publishing ? current.images.map((image) => {
          const selection = payload.images.find((entry) => String(entry.imageId) === String(image.id));
          return { ...image, isPublic: !!selection || image.isCover, displayOrder: selection?.displayOrder ?? null };
        }) : current.images }));
        setPublishOpen(false); moreButton.current?.focus();
        setSuccess(publishing ? "Project published. It is now visible in the public Projects section." : "Project unpublished. It is no longer visible in the public Projects section.");
      }} />}
      {action && project && <ProjectActionDialog action={action} project={project} onClose={() => setAction(null)} onSaved={(savedAction) => {
        setAction(null);
        setSuccess(savedAction === "edit" ? "Project saved." : savedAction === "photos" ? "Photos uploaded." : "Update added.");
        setTab(savedAction === "photos" ? "Images" : savedAction === "update" ? "Updates" : "Overview");
        setAttempt((value) => value + 1);
      }} />}
    </div>
  </AdminLayout>;
}
